import Cookies from 'js-cookie';
import type { RemoveBgJob } from '@/types/removebg';

const COOKIE_KEY = 'removebg_history';
const COOKIE_MAX_ITEMS = 20;
const COOKIE_EXPIRY_DAYS = 30;

export interface CookieHistoryItem {
    job_id: string;
    url_preview: string;
    url_hd: string;
    url_original?: string;
    processing_time_ms?: number;
    created_at: string;
    original_filename?: string;
    original_size_bytes?: number;
    original_mimetype?: string;
}

/**
 * Save job to cookies (for non-authenticated users)
 */
export function saveJobToCookies(job: RemoveBgJob): void {
    try {
        const history = getHistoryFromCookies();

        const newItem: CookieHistoryItem = {
            job_id: job.job_id,
            url_preview: job.url_preview,
            url_hd: job.url_hd,
            url_original: job.url_original,
            processing_time_ms: job.processing_time_ms,
            created_at: job.created_at || new Date().toISOString(),
            original_filename: job.original?.filename,
            original_size_bytes: job.original?.size_bytes,
            original_mimetype: job.original?.mimetype
        };

        // Add to beginning and limit to max items
        history.unshift(newItem);
        const limitedHistory = history.slice(0, COOKIE_MAX_ITEMS);

        // Save to cookie
        Cookies.set(COOKIE_KEY, JSON.stringify(limitedHistory), {
            expires: COOKIE_EXPIRY_DAYS,
            sameSite: 'Lax',
            secure: process.env.NODE_ENV === 'production'
        });
    } catch (error) {
        console.error('Error saving to cookies:', error);
    }
}

/**
 * Get history from cookies
 */
export function getHistoryFromCookies(): CookieHistoryItem[] {
    try {
        const cookieValue = Cookies.get(COOKIE_KEY);
        if (!cookieValue) return [];

        const history = JSON.parse(cookieValue);
        return Array.isArray(history) ? history : [];
    } catch (error) {
        console.error('Error reading from cookies:', error);
        return [];
    }
}

/**
 * Delete specific job from cookies
 */
export function deleteJobFromCookies(jobId: string): void {
    try {
        const history = getHistoryFromCookies();
        const filtered = history.filter(item => item.job_id !== jobId);

        if (filtered.length > 0) {
            Cookies.set(COOKIE_KEY, JSON.stringify(filtered), {
                expires: COOKIE_EXPIRY_DAYS,
                sameSite: 'Lax',
                secure: process.env.NODE_ENV === 'production'
            });
        } else {
            Cookies.remove(COOKIE_KEY);
        }
    } catch (error) {
        console.error('Error deleting from cookies:', error);
    }
}

/**
 * Clear all history from cookies
 */
export function clearCookieHistory(): void {
    Cookies.remove(COOKIE_KEY);
}

/**
 * Convert cookie item to RemoveBgHistoryItem format
 */
export function cookieItemToJob(item: CookieHistoryItem): import('@/types/removebg').RemoveBgHistoryItem {
    return {
        job_id: item.job_id,
        url_preview: item.url_preview,
        url_hd: item.url_hd,
        url_original: item.url_original,
        processing_time_ms: item.processing_time_ms,
        created_at: item.created_at,
        status: 'success',
        original: item.original_filename ? {
            filename: item.original_filename,
            size_bytes: item.original_size_bytes || 0,
            mimetype: item.original_mimetype || 'image/png'
        } : undefined
    };
}
